import { useSyncExternalStore } from "react";
import { useApiClient } from "@playbookai/api-clients";
import type { AuthScope } from "@playbookai/types";

/**
 * Auth-scope discriminator for the KPI feature's identity-bearing caches
 * (PB-2941): the swept distinct-owner ids and the resolved owner display
 * names. The shell's QueryClient is created once and SURVIVES in-page auth
 * transitions, so these keys embed the host auth scope — the same
 * scope-keyed-cache pattern SOLVE (`solveScopePrefix`) and Hoshin
 * (`hoshinShareKey`) use. No persistence, no new API: the scope comes from
 * the existing `ApiClientConfig.authScope` signal.
 */

const NO_SCOPE_SUBSCRIBE = (): (() => void) => () => undefined;
const NO_SCOPE_SNAPSHOT = (): null => null;

/** Format a scope into the cache-key discriminator. `"unscoped"` keeps hosts
 * and tests that inject no `authScope` working (a single-identity page). */
export function kpiAuthScopeKey(scope: AuthScope | null): string {
  return scope
    ? `${scope.tenantId}:${scope.actorUserId}:${scope.authSessionGeneration}`
    : "unscoped";
}

/**
 * Reactive auth scope — subscribes to the host's auth-scope source (the SOLVE
 * `useSolveAuthScope` subscription shape), so an in-page tenant/session
 * transition is observed on the very next render rather than one identity later.
 */
export function useKpiAuthScope(): AuthScope | null {
  const config = useApiClient();
  const source = config.authScope;
  return useSyncExternalStore(
    source?.subscribe ?? NO_SCOPE_SUBSCRIBE,
    source?.getSnapshot ?? NO_SCOPE_SNAPSHOT,
    source?.getSnapshot ?? NO_SCOPE_SNAPSHOT,
  );
}

/** Scope key for the identity-bearing caches — see the file header. */
export function useKpiAuthScopeKey(): string {
  return kpiAuthScopeKey(useKpiAuthScope());
}

/**
 * PB-2941 — the signed-in user's id, for the "is this me?" presentation checks
 * (today: whether to offer the Update value affordance on a KPI this user owns).
 *
 * `null` before the auth bootstrap completes, and the caller must treat that as
 * "not the owner" rather than as "unknown, so allow" — a permissive default
 * would flash a control the server then refuses.
 *
 * PRESENTATION ONLY. `POST /v1/kpis/:id/observations` compares the JWT actor to
 * the KPI's `ownerUserId` server-side; this hook cannot grant anything and a
 * host that never wires `authScope` simply hides an affordance the user could
 * still reach by URL and would still be authorized for.
 */
export function useKpiActorUserId(): string | null {
  const config = useApiClient();
  const scope = useKpiAuthScope();
  // `authScope` is the reactive source and wins. `getCurrentUser` is the older,
  // non-reactive seam the Create page's Owner default already uses — read as a
  // fallback so a host wiring only that one still resolves an identity.
  return scope?.actorUserId ?? config.getCurrentUser?.()?.id ?? null;
}
